"use client";

import Link from "next/link";
import { useLocale } from "@/lib/i18n";
import { localeHref } from "@/lib/i18n/config";
import { WolfGlyph, WolfMark } from "@/components/brand/Logo";

const connect = [
  { href: "mailto:hello@amalbryx.com", label: "hello@amalbryx.com" },
  { href: "tel:+355693678730", label: "+355 69 367 8730" },
  { href: "https://linkedin.com/company/amalbryx", label: "LinkedIn" },
  { href: "https://x.com/amalbryx", label: "X" },
  { href: "https://instagram.com/amalbryx", label: "Instagram" },
];

export function Footer() {
  const { t, locale } = useLocale();

  const studio = [
    { href: "/#work", label: t.nav.work },
    { href: "/#capabilities", label: t.nav.capabilities },
    { href: "/#process", label: t.nav.process },
    { href: "/services", label: t.nav.pricing },
    { href: "/services#faq", label: t.nav.faq },
    { href: "/notes", label: "Notes" },
    { href: "/contact", label: t.nav.contact },
  ];

  return (
    <footer className="mx-auto max-w-[1440px] px-[var(--spacing-page-x)] pt-24 pb-10 border-t border-[var(--color-hairline)]">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
        <div>
          <div className="flex items-center gap-3">
            <WolfMark className="h-10 w-auto [filter:drop-shadow(0_0_10px_rgba(59,130,246,0.18))]" />
            <span
              className="font-medium tracking-[0.16em] text-3xl md:text-4xl"
              style={{ fontFamily: "var(--font-display)" }}
            >
              AMALBRYX
            </span>
          </div>
          <p className="microcaps mt-3">{t.footer.tagline}</p>
        </div>
        <div>
          <h2 className="microcaps">{t.footer.studio}</h2>
          <ul className="mt-4 space-y-2">
            {studio.map((l) => (
              <li key={l.href}>
                <Link
                  className="link-underline text-[var(--color-text-dim)] hover:text-[var(--color-text)]"
                  href={localeHref(locale, l.href)}
                >
                  {l.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>
        <div>
          <h2 className="microcaps">{t.footer.connect}</h2>
          <ul className="mt-4 space-y-2">
            {connect.map((l) => (
              <li key={l.href}>
                <a
                  className="link-underline text-[var(--color-text-dim)] hover:text-[var(--color-text)]"
                  href={l.href}
                >
                  {l.label}
                </a>
              </li>
            ))}
          </ul>
        </div>
        <div>
          <h2 className="microcaps">{t.footer.locations}</h2>
          <ul className="mt-4 space-y-2 text-[var(--color-text-dim)]">
            <li>{t.footer.locTirana}</li>
            <li>{t.footer.locRemote}</li>
            <li className="flex items-center gap-2">
              <span
                data-signal-dock
                className="inline-block h-1.5 w-1.5 rounded-full bg-[var(--color-blue)] animate-pulse-soft"
                aria-hidden
              />
              {t.footer.available}
            </li>
          </ul>
        </div>
      </div>
      <div className="mt-16 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 border-t border-[var(--color-hairline)] pt-6">
        <span className="flex items-center gap-2.5 microcaps">
          <WolfGlyph className="h-4 w-4 text-[var(--color-text-mute)] transition-colors hover:text-[var(--color-blue)]" />
          {t.footer.copyright}
        </span>
        <div className="flex items-center gap-4 microcaps">
          <Link
            href={localeHref(locale, "/privacy")}
            className="link-underline text-[var(--color-text-mute)] hover:text-[var(--color-text)] transition-colors"
          >
            Privacy
          </Link>
          <span aria-hidden className="text-[var(--color-text-mute)]">·</span>
          <Link
            href={localeHref(locale, "/terms")}
            className="link-underline text-[var(--color-text-mute)] hover:text-[var(--color-text)] transition-colors"
          >
            Terms
          </Link>
          <span aria-hidden className="text-[var(--color-text-mute)]">·</span>
          <span className="text-[var(--color-blue)]">{t.footer.intake}</span>
        </div>
      </div>
    </footer>
  );
}
